import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Car, ArrowRight, WhatsappLogo, Phone, EnvelopeSimple, Briefcase,
} from "@phosphor-icons/react";
import { openWhatsApp, toWhatsAppNumber } from "@/lib/contact";
import { useI18n } from "@/lib/i18n";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import ThemeToggle from "@/components/ThemeToggle";
import PublicFooter from "@/components/PublicFooter";
import api from "@/lib/api";

/**
 * Phase 27 + 28.5 — Branded company entry page.
 *
 * This page is a **structural clone** of `LandingPage.js` (S.DRIVE root)
 * with all copy/branding sourced from `/api/public/companies/:slug`.
 * Every layout decision — header, hero block, 3-feature grid, contact
 * card, footer — comes directly from the S.DRIVE template; only the
 * accent colour and content strings switch per tenant.
 *
 * Fallback strategy: if a tenant field is empty we fall back to the
 * S.DRIVE i18n strings (the user requested "fallback to S.DRIVE
 * defaults if fields are missing"). For trust_points (= 3 features)
 * we fall back to the i18n features S.DRIVE uses; for tenants that
 * configure their own `trust_points` we render up to the first 3.
 */

function hexToRgba(hex, alpha = 0.12) {
  const h = (hex || "").replace("#", "");
  if (h.length !== 6) return `rgba(245,158,11,${alpha})`;
  const r = parseInt(h.slice(0, 2), 16), g = parseInt(h.slice(2, 4), 16), b = parseInt(h.slice(4, 6), 16);
  return `rgba(${r},${g},${b},${alpha})`;
}

function Feature({ title, desc }) {
  return (
    <div className="bg-[#121212] border border-zinc-800 rounded-lg p-5">
      <h3 className="text-white font-medium mb-1.5">{title}</h3>
      <p className="text-sm text-zinc-400">{desc}</p>
    </div>
  );
}

/** Trust points come either as an array of strings or as `{title, desc}`
 *  objects. Normalise to render in <Feature />. */
function normaliseTrustPoint(tp, fallbackTitle) {
  if (!tp) return null;
  if (typeof tp === "string") {
    // "Title — Description" → split on em-dash; otherwise use as title.
    const m = tp.split(/\s+[—–-]\s+/);
    return { title: m[0]?.trim() || fallbackTitle, desc: m[1]?.trim() || "" };
  }
  return { title: tp.title || fallbackTitle, desc: tp.desc || "" };
}

export default function CompanyLanding() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { t } = useI18n();
  const [company, setCompany] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let cancel = false;
    // Phase 2D — Mémoriser le slug tenant pour la redirection logout (cf.
    // AuthContext.logout). sessionStorage uniquement, per-tab, public-only.
    try {
      if (slug && typeof slug === "string") {
        sessionStorage.setItem("sdrive_tenant_slug", slug);
      }
    } catch { /* sessionStorage indisponible — pas critique */ }
    (async () => {
      try {
        const { data } = await api.get(`/public/companies/${slug}`);
        if (!cancel) setCompany(data);
      } catch {
        if (!cancel) setNotFound(true);
      }
    })();
    return () => { cancel = true; };
  }, [slug]);

  if (notFound) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#050505] text-white px-6">
        <div className="text-center">
          <p className="text-lg font-medium">Société introuvable</p>
          <p className="text-sm text-zinc-500 mt-2">La société « {slug} » n'existe pas ou a été suspendue.</p>
        </div>
      </div>
    );
  }
  if (!company) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#050505]">
        <div className="w-8 h-8 border-2 border-amber-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // ── Resolve dynamic fields with safe fallbacks ────────────────────────
  const name      = company.company_name || "Sdrive Transfert";
  const brand     = company.branding || {};
  const accent    = brand.primary_color || "#F59E0B";
  const onAccent  = brand.secondary_color || "#000000";
  const accentSoft = hexToRgba(accent, 0.18);
  const logoUrl   = brand.logo_url || "";
  const phone     = company.phone || "";
  const email     = company.email || "";
  const whatsapp  = company.whatsapp_number || phone;
  const phoneRaw  = (phone || "").replace(/\s/g, "");
  const waNumber  = toWhatsAppNumber(whatsapp);

  // The S.DRIVE root uses an "eyebrow + 3-line H2 with accent" pattern.
  // For the tenant we rebuild that exact structure: the `headline` is the
  // bold first chunk, optionally followed by `subheadline` as the body.
  // If both are empty we drop in the i18n S.DRIVE copy.
  const eyebrow      = (brand.marketing_text || "").trim() || t("landing.eyebrow");
  const heroTitle    = (company.headline || "").trim() || `${t("landing.title_1")} ${t("landing.title_accent")} ${t("landing.title_2")}`;
  const heroIntro    = (company.subheadline || "").trim() || t("landing.intro");

  // Three "features" cards. Spec wants `trust_points` editable from /super.
  // Render up to 3 trust points; pad missing slots with the S.DRIVE i18n
  // defaults so the layout never collapses.
  const tpRaw = (company.trust_points || []).slice(0, 3);
  const sdriveDefaults = [
    { title: t("landing.feat_noacc_t"), desc: t("landing.feat_noacc_d") },
    { title: t("landing.feat_price_t"), desc: t("landing.feat_price_d") },
    { title: t("landing.feat_drv_t"),   desc: t("landing.feat_drv_d") },
  ];
  const features = sdriveDefaults.map((d, i) => normaliseTrustPoint(tpRaw[i], d.title) || d).map((tp, i) => ({
    title: tp.title || sdriveDefaults[i].title,
    desc:  tp.desc  || sdriveDefaults[i].desc,
  }));

  // Inline override for the amber accent so existing Tailwind classes
  // (text-amber-400, bg-amber-500, etc.) get repainted to the tenant
  // colour without touching the S.DRIVE template structure.
  const cssOverrides = `
    [data-tenant-landing] .tenant-accent-text  { color: ${accent}; }
    [data-tenant-landing] .tenant-accent-bg    { background: ${accent}; color: ${onAccent}; }
    [data-tenant-landing] .tenant-accent-bg:hover { background: ${accent}; filter: brightness(1.08); }
    [data-tenant-landing] .tenant-accent-soft  { background: ${accentSoft}; }
    [data-tenant-landing] .tenant-accent-eyeb  { color: ${accent}; }
  `;

  return (
    <div data-testid="company-landing" data-tenant-landing className="min-h-screen bg-[#050505] text-white">
      <style>{cssOverrides}</style>

      {/* ── Header — mirrors S.DRIVE LandingPage header ───────────────── */}
      <header className="border-b border-zinc-800/50 bg-[#050505]/90 backdrop-blur-xl sticky top-0 z-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center gap-2 sm:gap-3">
          <button
            data-testid="landing-logo-home"
            onClick={() => navigate(`/${slug}`)}
            className="flex items-center gap-2 sm:gap-3 min-w-0 focus:outline-none focus:ring-2 focus:ring-white/30 rounded"
            aria-label={`${name} — Accueil`}
          >
            {logoUrl ? (
              <img src={logoUrl} alt={name} className="h-9 w-auto" />
            ) : (
              <span className="h-9 w-9 rounded-full flex items-center justify-center text-sm font-semibold tenant-accent-bg">
                {(name.trim()[0] || "?").toUpperCase()}
              </span>
            )}
            <h1 data-testid="landing-company-name" className="text-base sm:text-lg font-medium truncate">{name}</h1>
          </button>
          <div className="flex-1" />
          <LanguageSwitcher testid="landing-lang" />
          <ThemeToggle testid="company-landing-theme" />
          <Button
            data-testid="landing-login"
            onClick={() => navigate(`/${slug}/login`)}
            className="bg-zinc-800 text-white hover:bg-zinc-700 border border-zinc-700 font-semibold h-9 px-3 sm:px-4 shrink-0"
          >
            <Briefcase size={16} weight="fill" className="mr-1.5 tenant-accent-text" />
            <span className="hidden sm:inline">{t("landing.pro_area")}</span>
            <span className="sm:hidden">Pro</span>
          </Button>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 pt-10 sm:pt-16 pb-10">
        {/* ── Hero — eyebrow + bold H2 + intro ─────────────────────────── */}
        <div className="text-center">
          <p data-testid="landing-eyebrow" className="text-xs sm:text-sm uppercase tracking-widest mb-3 tenant-accent-eyeb">{eyebrow}</p>
          <h2 data-testid="landing-headline" className="text-4xl sm:text-5xl lg:text-6xl font-semibold leading-tight mb-5">
            {heroTitle}
          </h2>
          <p data-testid="landing-subheadline" className="text-zinc-400 text-base sm:text-lg max-w-2xl mx-auto mb-8">
            {heroIntro}
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button
              data-testid="cta-book"
              onClick={() => navigate(`/${slug}/booking`)}
              className="font-semibold h-12 px-6 text-base tenant-accent-bg"
            >
              <Car size={20} className="mr-2" weight="fill" /> {t("landing.cta_book")}
              <ArrowRight size={18} className="ml-2" />
            </Button>
            {waNumber && (
              <Button
                data-testid="cta-whatsapp"
                variant="outline"
                onClick={() => openWhatsApp(whatsapp, "Bonjour, je souhaite réserver une course VTC.")}
                className="border-[#25D366]/40 text-[#25D366] hover:bg-[#25D366]/10 h-12 px-6 text-base"
              >
                <WhatsappLogo size={20} className="mr-2" weight="fill" /> {t("landing.cta_wa")}
              </Button>
            )}
          </div>
        </div>

        {/* ── 3 features grid — same as S.DRIVE ────────────────────────── */}
        <div data-testid="landing-features" className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-16">
          {features.map((f, i) => (
            <Feature key={i} title={f.title} desc={f.desc} />
          ))}
        </div>

        {/* ── Services list (only if the tenant filled it) ─────────────── */}
        {(company.services && company.services.length > 0) && (
          <div data-testid="landing-services" className="mt-12">
            <p className="text-sm text-zinc-500 uppercase tracking-wider mb-3 text-center">Nos services</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 max-w-3xl mx-auto">
              {company.services.map((s, i) => (
                <div key={i} data-testid={`service-${i}`}
                     className="text-center text-sm text-zinc-300 border border-zinc-800 rounded-md py-2 px-3 bg-[#0f0f0f]">
                  {s}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── Contact card — exact same shape as S.DRIVE ───────────────── */}
        <div data-testid="landing-contact" className="mt-16 bg-[#121212] border border-zinc-800 rounded-xl p-6 text-center">
          <p className="text-sm text-zinc-500 uppercase tracking-wider mb-3">{t("landing.contact")}</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            {phone && (
              <a data-testid="contact-call" href={`tel:${phoneRaw}`}
                 className="inline-flex items-center justify-center gap-2 h-10 px-4 rounded-md bg-emerald-500/10 text-emerald-400 border border-emerald-500/30 hover:bg-emerald-500/20 text-sm font-medium">
                <Phone size={16} weight="fill" /> {phone}
              </a>
            )}
            {waNumber && (
              <a data-testid="contact-wa" href={`https://wa.me/${waNumber}`}
                 target="_blank" rel="noopener noreferrer"
                 className="inline-flex items-center justify-center gap-2 h-10 px-4 rounded-md bg-[#25D366]/10 text-[#25D366] border border-[#25D366]/30 hover:bg-[#25D366]/20 text-sm font-medium">
                <WhatsappLogo size={16} weight="fill" /> WhatsApp
              </a>
            )}
            {email && (
              <a data-testid="contact-email" href={`mailto:${email}`}
                 className="inline-flex items-center justify-center gap-2 h-10 px-4 rounded-md bg-sky-500/10 text-sky-400 border border-sky-500/30 hover:bg-sky-500/20 text-sm font-medium">
                <EnvelopeSimple size={16} weight="fill" /> {email}
              </a>
            )}
          </div>
        </div>
      </main>

      {/* ── Footer — kept identical to S.DRIVE template ─────────────── */}
      <footer className="max-w-6xl mx-auto px-4 sm:px-6 py-4 text-center text-[11px] text-zinc-600">
        © {name} — {t("landing.footer")} ·{" "}
        <button onClick={() => navigate(`/${slug}/booking`)} className="underline hover:text-zinc-400">{t("landing.req_link")}</button>
        {" · "}
        <button onClick={() => navigate(`/${slug}/login`)} className="underline hover:text-zinc-400">{t("landing.pro_area")}</button>
      </footer>
      <PublicFooter className="border-t-zinc-900/40" />
    </div>
  );
}
